// «Мой день» — Кабинет сотрудника.
// SPA view для /my-day. Загружает 5 endpoints параллельно через
// Promise.all(settle), рендерит 7 секций по дизайну Roxber Teal,
// поддерживает inline-edit % через PATCH /api/tasks/{id} + таймер с
// логированием в daily_log через POST /api/my-day/timer/stop.

import { api } from '../api.js';
import { getCurrentUser } from '../auth.js';
import { router } from '../router.js';
import { formatMinutes } from '../utils/format.js';

let _heroTicker = null;
// Выбранная задача для hero (null = backend-default, in_progress first).
// Click на задачу в очереди → меняем это и перерендериваем.
let _selectedTaskId = null;
// Текущий контейнер view — нужен для silent reload без router.push().
let _currentContainer = null;

const MONTH_RU = [
  'янв', 'фев', 'мар', 'апр', 'май', 'июн',
  'июл', 'авг', 'сен', 'окт', 'ноя', 'дек',
];
const WEEKDAY_RU_SHORT = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];

const settle = (p) => p.then((v) => ({ ok: true, v })).catch((e) => ({ ok: false, e }));

export async function initMyDay(container, opts) {
  // opts может быть null (router) — нормализуем.
  const options = opts || {};
  if (_heroTicker) { clearInterval(_heroTicker); _heroTicker = null; }
  _currentContainer = container;

  const user = getCurrentUser();
  // silent=true → не показываем loading state (для in-place updates без мерцания).
  if (!options.silent) {
    container.innerHTML = '<div class="md-loading">Загрузка кабинета...</div>';
  }
  const scrollY = window.scrollY;

  const today = new Date();
  const start = today.toISOString().slice(0, 10);
  // Capacity heatmap = 14 дней (2 недели от сегодня вперёд).
  const end = new Date(today.getTime() + 13 * 86400_000).toISOString().slice(0, 10);

  const wlPromise = user?.resource_id
    ? settle(api.resourceWorkload(user.resource_id, start, end))
    : Promise.resolve({ ok: true, v: [] });

  const [hero, queue, production, workload, activity, notifications] = await Promise.all([
    settle(api.myDay.hero(_selectedTaskId)),
    settle(api.myDay.queue()),
    settle(api.myDay.production(12)),
    wlPromise,
    settle(api.myDay.activity(15)),
    settle(api.notifications.list({ limit: 15 })),
  ]);

  const state = detectState({ hero, queue, workload });

  container.innerHTML = `
    <div class="my-day-root" data-state="${state}">
      <div class="md-main">
        ${state === 'alert' ? renderBanner('bad', 'Буфер проекта в красной зоне', 'На критической цепи — твоя задача. Сегодня — приоритет №1.') : ''}
        ${state === 'overload' ? renderBanner('warn', 'Перегруз на ближайшие дни', 'Дневная загрузка превышает капасити. Пересогласуй сроки или передай задачу.') : ''}
        ${renderHeroSection(hero, state, queue, workload)}
        ${renderQueueSection(queue)}
        ${renderProductionSection(production)}
        ${renderMiniGanttSection(queue)}
      </div>
      <div class="md-side">
        ${renderProfileSection(user, hero)}
        ${renderCapacitySection(workload)}
        ${renderFeverMiniSection(hero)}
        ${renderNotificationsSection(notifications)}
        ${renderActivitySection(activity)}
      </div>
    </div>
  `;

  // SVG / Chart injection после установки innerHTML.
  if (production.ok) drawProductionChart(production.v);
  if (queue.ok) drawMiniGantt(queue.v);
  if (hero.ok && hero.v.my_projects_fever?.length) drawFeverMini(hero.v.my_projects_fever);

  attachHeroHandlers(container);
  attachQueueHandlers(container);
  attachActivityHandlers(container);
  startHeroTicker(container, hero.ok ? hero.v.task : null);

  // Restore scroll position after silent re-render (для action handlers).
  if (options.silent) {
    window.scrollTo({ top: scrollY, behavior: 'instant' });
  }
}

// ── State detection ─────────────────────────────────────────────────────────

function detectState({ hero, queue, workload }) {
  if (hero.ok && hero.v.alert_flag) return 'alert';
  if (workload.ok && weekIsOverloaded(workload.v)) return 'overload';
  const noTask = !hero.ok || !hero.v.task;
  const noQueue = !queue.ok || queue.v.length === 0;
  if (noTask && noQueue) return 'empty';
  return 'normal';
}

function weekIsOverloaded(workload) {
  // Если в ближайших 7 днях есть хоть один день с planned > capacity.
  const first7 = workload.slice(0, 7);
  return first7.some((d) => Number(d.planned_minutes || 0) > Number(d.capacity_minutes || 0));
}

// ── Render: Banner ─────────────────────────────────────────────────────────

function renderBanner(kind, title, body) {
  return `
    <div class="md-banner ${kind}">
      <span class="icon">!</span>
      <div><b>${escHtml(title)}.</b> ${escHtml(body)}</div>
    </div>
  `;
}

// ── Render: Hero ───────────────────────────────────────────────────────────

function renderHeroSection(hero, state, queue, workload) {
  if (!hero.ok) return renderSectionError('hero', hero.e);
  const data = hero.v;
  if (!data.task) return renderEmptyHero();

  const t = data.task;
  const buf = data.project_buffer;
  const bufConsumed = buf?.consumed_pct != null ? Number(buf.consumed_pct) : null;
  const ccPos = buf?.cc_position_pct != null ? Number(buf.cc_position_pct) : null;
  const todayMins = data.logged_today_minutes || 0;
  const capMins = data.daily_capacity_minutes || 0;

  const todayLabel = todayMins > 0 ? formatMinutes(todayMins) : '0ч';
  const capLabel = capMins > 0 ? formatMinutes(capMins) : '0ч';

  // === НОВЫЕ МЕТАДАННЫЕ (1-в-1 с мокапом) ===

  // WIP: текущее число in_progress + лимит 3 (как в global stats).
  const wipCurrent = queue.ok ? queue.v.filter((x) => x.status === 'in_progress').length : 0;
  const wipLimit = 3;
  const wipOver = wipCurrent > wipLimit;

  // Часов на неделе: SUM(planned) / SUM(capacity) на ближайших 7 днях.
  // Cap display на 999ч (выше — артефакт кривых planned_minutes, см. B3 audit).
  let weekHrsTxt = '—';
  let weekOver = false;
  if (workload.ok && workload.v && workload.v.length) {
    const first7 = workload.v.slice(0, 7);
    const plan = first7.reduce((s, d) => s + Number(d.planned_minutes || 0), 0);
    const cap = first7.reduce((s, d) => s + Number(d.capacity_minutes || 0), 0);
    if (cap > 0 || plan > 0) {
      const planH = Math.round(plan / 60);
      const capH = Math.round(cap / 60);
      const planLbl = planH > 999 ? '999+' : String(planH);
      weekHrsTxt = `${planLbl} / ${capH}`;
      weekOver = plan > cap;
    }
  }

  // Задач горит: red CC-задачи в очереди юзера.
  const hotCount = queue.ok
    ? queue.v.filter((x) => x.is_on_critical_chain && x.project_fever_zone === 'red').length
    : 0;
  // Если нет красных, но есть жёлтые CC — показываем как warn.
  const yellowCount = queue.ok
    ? queue.v.filter((x) => x.is_on_critical_chain && x.project_fever_zone === 'yellow').length
    : 0;
  const hotShow = hotCount > 0 ? hotCount : yellowCount;
  const hotCls = hotCount > 0 ? 'bad' : (yellowCount > 0 ? 'warn' : '');

  // Приоритет: CC · #N (N — индекс в queue среди CC-задач), иначе uppercase project_priority.
  let priorityLabel = '—';
  let priorityCls = '';
  if (t.is_on_cc) {
    // Найдём позицию текущей задачи среди CC-задач (отсортированы по приоритету).
    let ccIdx = 1;
    if (queue.ok) {
      const ccTasks = queue.v.filter((x) => x.is_on_critical_chain);
      const found = ccTasks.findIndex((x) => x.id === t.id);
      if (found >= 0) ccIdx = found + 1;
    }
    priorityLabel = `CC · #${ccIdx}`;
    priorityCls = 'cc';
  } else if (t.project_priority && t.project_priority !== 'backlog') {
    priorityLabel = String(t.project_priority).toUpperCase();
  }

  // Breadcrumb: проект · задача N.M (sort_order — если есть, иначе fallback).
  const taskNum = t.sort_order || t.id;
  const breadcrumb = t.parent_task_name
    ? `<a data-route="/gantt/${t.project_id}">${escHtml(t.project_name)}</a> · ${escHtml(t.parent_task_name)}`
    : `<a data-route="/gantt/${t.project_id}">${escHtml(t.project_name)}</a> · задача ${taskNum}${t.is_on_cc ? ' · критическая цепь' : ''}`;

  // === BUFFER LABEL + FOOTER MESSAGE ===
  const bufferHeader = ccPos != null
    ? `<b>Буфер проекта</b> · CC прошла <b>${Math.round(ccPos)}%</b>`
    : `<b>Буфер проекта</b> · съедено`;

  let bufMsg = '';
  if (bufConsumed != null) {
    if (buf.fever_zone === 'red') {
      bufMsg = 'Буфер в красной зоне — сегодня твой приоритет.';
    } else if (weekOver) {
      bufMsg = 'Запас OK. Но твоя загрузка превышает капасити.';
    } else {
      bufMsg = 'Запас OK.';
    }
  }

  // CC marker позиция: min 4% и max 96%, иначе бейдж наезжает на края.
  const ccMarkerLeft = ccPos != null
    ? Math.min(96, Math.max(4, Number(ccPos)))
    : null;

  return `
    <div class="md-hero" data-state="${state}">
      <div>
        <div class="md-hero-eyebrow"><span class="dot"></span>${t.status === 'in_progress' ? 'Сейчас работаю' : 'Следующая по приоритету'}</div>
        <h2>${escHtml(t.name)}</h2>
        <div class="ctx">${breadcrumb}</div>

        <div class="md-hero-meta">
          <div class="m">
            <div class="lbl">WIP</div>
            <div class="val ${wipOver ? 'bad' : ''}">${wipCurrent} <small>/ ${wipLimit}</small></div>
          </div>
          <div class="m">
            <div class="lbl">Часов на неделе</div>
            <div class="val ${weekOver ? 'warn' : ''}">${weekHrsTxt}</div>
          </div>
          <div class="m">
            <div class="lbl">Задач горит</div>
            <div class="val ${hotCls}">${hotShow}</div>
          </div>
          <div class="m">
            <div class="lbl">Приоритет</div>
            <div class="val ${priorityCls}">${escHtml(priorityLabel)}</div>
          </div>
        </div>

        ${buf && bufConsumed != null ? `
          <div class="md-hero-buffer">
            <div class="b-h">
              <span>${bufferHeader}</span>
              <span class="pct" style="${bufConsumed > 80 ? 'color:#FF8A8A' : ''}">${Math.round(bufConsumed)}%</span>
            </div>
            <div class="bar">
              <div class="progress" style="width:${Math.min(100, bufConsumed)}%"></div>
              ${ccMarkerLeft != null ? `
                <div class="cc-marker"
                     style="left:${ccMarkerLeft}%"
                     title="Критическая цепь: ${Math.round(ccPos)}%">
                  <span class="cc-marker-lbl">CC ${Math.round(ccPos)}%</span>
                </div>
              ` : ''}
            </div>
            <div class="b-foot">
              <span>0%</span>
              <span class="msg">${escHtml(bufMsg)}</span>
              <span>100%</span>
            </div>
          </div>
        ` : ''}

        ${renderHeroActions(t, data.active_session, taskNum)}
        ${renderHeroCommentBox(t)}
      </div>

      ${renderHeroTimer(t, data.active_session, todayLabel, capLabel)}
    </div>
  `;
}

function renderHeroCommentBox(t) {
  // Inline textarea + workflow-кнопки: завершить / процент / коммент.
  const pct = Math.max(0, Math.min(100, Math.round(Number(t.work_pct || 0))));
  return `
    <div class="md-hero-workflow" data-task-id="${t.id}">
      <div class="md-hero-progress">
        <span class="lbl">Прогресс</span>
        <span class="pct-edit" contenteditable spellcheck="false" data-md-action="hero-edit-pct" data-task-id="${t.id}">${pct}%</span>
        <button class="md-btn ghost compact" data-md-action="complete" data-task-id="${t.id}">✓ Завершить</button>
      </div>
      <div class="md-hero-comment">
        <textarea class="md-hero-comment-input"
                  data-task-id="${t.id}"
                  placeholder="Написать комментарий или @упомянуть коллегу… (Cmd/Ctrl+Enter — отправить)"
                  rows="2"></textarea>
        <button class="md-btn compact" data-md-action="hero-send-comment" data-task-id="${t.id}">Отправить</button>
      </div>
    </div>
  `;
}

function renderHeroActions(t, session, taskNum) {
  // 1-в-1 с мокапом: 4 кнопки в одну линию.
  // Primary (teal solid): «▶ Продолжить» (резюм) / «▶ Возобновить» (paused) / «▶ Начать» (нет session).
  // Передать задачу N.M — warn-outline (orange).
  // Запросить +ресурс — ghost.
  // Переплanировать неделю — ghost.
  // ⏸/⏹ перенесены в timer-блок (renderHeroTimer).
  let primaryLabel = '▶ Начать работу';
  let primaryAction = 'session-start';
  if (session) {
    if (session.is_paused) {
      primaryLabel = '▶ Возобновить';
      primaryAction = 'session-resume';
    } else {
      primaryLabel = '▶ Продолжить';
      primaryAction = 'session-pause';  // running → click = пауза-resume цикл, но primary смотрится как «работаю»
      // Сейчас как кнопка — она бесполезна (уже работаешь). Делаем её disabled-styling, но кликабельной (alias на pause).
    }
  }
  const primaryCls = session && !session.is_paused ? 'md-btn primary-running' : 'md-btn';

  return `
    <div class="md-hero-actions">
      <button class="${primaryCls}" data-md-action="${primaryAction}" data-task-id="${t.id}">${primaryLabel}</button>
      <button class="md-btn warn-outline" data-md-action="transfer" data-task-id="${t.id}">Передать задачу ${escHtml(String(taskNum))}</button>
      <button class="md-btn ghost" data-md-action="request-resource" data-task-id="${t.id}">Запросить +ресурс</button>
      <button class="md-btn ghost" data-md-action="reschedule-week" data-task-id="${t.id}">Перепланировать неделю</button>
    </div>
  `;
}

function renderHeroTimer(t, session, todayLabel, capLabel) {
  const hasSession = !!session;
  const isPaused = hasSession && session.is_paused;
  // Источник правды для таймера — ТОЛЬКО активная сессия. Если её нет —
  // «Не запущена» и 00:00:00, даже если task.status='in_progress'
  // (задачу могли стартануть давно, но активной сессии сейчас нет —
  // показывать календарный elapsed бессмысленно).
  const stateLabel = !hasSession
    ? 'Не запущена'
    : isPaused ? 'Пауза' : 'В работе';
  const stateCls = isPaused ? 'paused' : (hasSession ? 'running' : '');

  // Таймер минут — только из активной сессии. Без сессии — 0.
  const accMin = hasSession ? Number(session.accumulated_minutes || 0) : 0;
  const startedAtIso = hasSession ? (session.started_at || '') : '';

  // Круглые icon-кнопки под таймером — при наличии сессии (любое состояние).
  // SVG-иконки для точного центрирования (Unicode glyphs дают разное смещение
  // в разных шрифтах). 12px viewBox.
  const SVG_PLAY = '<svg viewBox="0 0 12 12" width="12" height="12" aria-hidden="true"><polygon points="3,2 10,6 3,10" fill="currentColor"/></svg>';
  const SVG_PAUSE = '<svg viewBox="0 0 12 12" width="12" height="12" aria-hidden="true"><rect x="3" y="2" width="2.5" height="8" fill="currentColor"/><rect x="6.5" y="2" width="2.5" height="8" fill="currentColor"/></svg>';
  const SVG_STOP = '<svg viewBox="0 0 12 12" width="10" height="10" aria-hidden="true"><rect x="2" y="2" width="8" height="8" fill="currentColor"/></svg>';

  let controls = '';
  if (hasSession) {
    const leftBtn = isPaused
      ? `<button class="md-timer-ctrl resume" data-md-action="session-resume" data-task-id="${t.id}" title="Возобновить">${SVG_PLAY}</button>`
      : `<button class="md-timer-ctrl" data-md-action="session-pause" data-task-id="${t.id}" title="Пауза">${SVG_PAUSE}</button>`;
    const stopBtn = `<button class="md-timer-ctrl stop" data-md-action="session-stop" data-task-id="${t.id}" title="Стоп — записать время в журнал">${SVG_STOP}</button>`;
    controls = `<div class="md-timer-controls">${leftBtn}${stopBtn}</div>
      <button class="md-timer-discard" data-md-action="session-discard" data-task-id="${t.id}"
              title="Сбросить таймер без записи времени в журнал">✕ Сбросить время</button>`;
  }

  return `
    <div class="md-hero-timer">
      <div class="md-timer-state ${stateCls}"><span class="dot"></span>${stateLabel}</div>
      <div class="md-timer-clock"
           data-md-acc-min="${accMin}"
           data-md-started-at="${startedAtIso}"
           data-md-paused="${isPaused ? '1' : '0'}"
           data-md-has-session="${hasSession ? '1' : '0'}">
        ${hasSession ? formatHMS(session.total_minutes_now || accMin) : '00:00:00'}
      </div>
      ${controls}
      <div class="md-timer-today">Сегодня: <b>${todayLabel}</b> / ${capLabel}</div>
    </div>
  `;
}

// HH:MM:SS из минут (для session timer, обновляется каждую секунду).
function formatHMS(totalMinutes) {
  const totalSec = Math.max(0, Math.floor(Number(totalMinutes || 0) * 60));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function renderEmptyHero() {
  return `
    <div class="md-empty-hero">
      <div class="blob">◫</div>
      <h2>На сегодня задач нет</h2>
      <p>
        Возможно, проекты ещё не активированы или у тебя нет назначений.
        Загляни во вкладку <a data-route="/pipeline">Pipeline</a> или
        напиши руководителю.
      </p>
    </div>
  `;
}

// ── Render: Queue ──────────────────────────────────────────────────────────

function renderQueueSection(queue) {
  if (!queue.ok) {
    return `
      <div class="md-card">
        <div class="md-card-h"><h3>Очередь · по проектам · приоритет CCPM</h3></div>
        ${renderSectionError('queue', queue.e)}
      </div>
    `;
  }
  const tasks = queue.v;
  if (!tasks.length) {
    return `
      <div class="md-card">
        <div class="md-card-h"><h3>Очередь · по проектам · приоритет CCPM</h3></div>
        <div class="md-loading">Нет активных задач в очереди</div>
      </div>
    `;
  }

  // Group by project_id, sort groups by first task's is_on_critical_chain DESC.
  const groups = new Map();
  for (const t of tasks) {
    if (!groups.has(t.project_id)) {
      groups.set(t.project_id, {
        project_id: t.project_id,
        project_name: t.project_name,
        fever_zone: t.project_fever_zone,
        buffer_consumed_pct: t.project_buffer_consumed_pct,
        tasks: [],
      });
    }
    groups.get(t.project_id).tasks.push(t);
  }

  // Totals.
  const totalSum = tasks.reduce((s, t) => s + Number(t.cost_rub || 0), 0);
  const avgWork = tasks.length
    ? tasks.reduce((s, t) => s + Number(t.work_pct || 0), 0) / tasks.length
    : 0;
  const inProgressCount = tasks.filter((t) => t.status === 'in_progress').length;
  const todoCount = tasks.filter((t) => t.status === 'todo').length;
  const blockedTotalCount = tasks.filter((t) => t.status === 'blocked').length;

  // Метрики для chips в card-h.
  const todayIso = new Date().toISOString().slice(0, 10);
  const tasksToday = tasks.filter((t) => t.deadline && String(t.deadline) <= todayIso).length;
  const tasksOverdue = tasks.filter((t) => t.deadline && String(t.deadline) < todayIso).length;
  // Готово за неделю — на фронте не считается (нет данных about completed). Используем production.kpi.
  // Передадим через DOM data-attr или просто оставим в renderProductionSection. Тут — sum_planned_cost.

  let idx = 0;
  const groupsHtml = Array.from(groups.values()).map((g) => {
    const groupCost = g.tasks.reduce((s, t) => s + Number(t.cost_rub || 0), 0);
    const groupAvg = g.tasks.reduce((s, t) => s + Number(t.work_pct || 0), 0) / g.tasks.length;
    const blockedCount = g.tasks.filter((t) => t.status === 'blocked').length;
    // Пипсы — по одной на задачу (max 12 — иначе много шума), цвет по fever_zone задачи.
    const priPips = g.tasks.slice(0, 12).map((t) => {
      const fz = t.project_fever_zone;
      const cls = fz === 'red' ? 'r' : fz === 'yellow' ? 'y' : fz === 'green' ? 'g' : 'k';
      return `<span class="pip ${cls}"></span>`;
    }).join('') + (g.tasks.length > 12 ? `<span style="color:var(--text-tertiary);font-size:10px;margin-left:3px">+${g.tasks.length - 12}</span>` : '');
    // Подгруппировка задач проекта по сводной задаче (parent_task_id).
    // Задачи без сводной (parent_task_id=null) рендерятся прямо под проектом;
    // остальные — под свёртываемым подзаголовком сводной задачи.
    const byParent = new Map();
    for (const t of g.tasks) {
      const pid = t.parent_task_id || 0;
      if (!byParent.has(pid)) {
        byParent.set(pid, { parent_task_id: pid, parent_task_name: t.parent_task_name, tasks: [] });
      }
      byParent.get(pid).tasks.push(t);
    }
    let rowsHtml = '';
    // Сначала листовые без сводной.
    const noParent = byParent.get(0);
    if (noParent) {
      rowsHtml += noParent.tasks.map((t) => { idx += 1; return renderQueueRow(t, idx); }).join('');
    }
    // Затем — группы по сводным задачам.
    for (const [pid, sub] of byParent) {
      if (pid === 0) continue;
      const subCost = sub.tasks.reduce((s, t) => s + Number(t.cost_rub || 0), 0);
      rowsHtml += `
        <div class="md-q-subgroup" data-project-id="${g.project_id}" data-parent-id="${pid}">
          <span class="toggle" data-md-action="toggle-subgroup">▾</span>
          <span class="sname" title="${escAttr(sub.parent_task_name || '')}">${escHtml(sub.parent_task_name || 'Сводная задача')}</span>
          <span class="scnt">· ${sub.tasks.length}</span>
          <span class="ssum">${formatRubShort(subCost)} ₽</span>
        </div>
      `;
      rowsHtml += sub.tasks.map((t) => { idx += 1; return renderQueueRow(t, idx, pid); }).join('');
    }
    const blockedBadge = blockedCount > 0
      ? `<span class="badge-blocked" title="В группе есть заблокированные задачи (${blockedCount} шт.)">🔒 заблокировано${blockedCount > 1 ? ` · ${blockedCount}` : ''}</span>`
      : '';
    return `
      <div class="md-q-group${blockedCount > 0 ? ' has-blocked' : ''}" data-project-id="${g.project_id}">
        <div class="gname">
          <span class="toggle" data-md-action="toggle-group">▾</span>
          <span class="ttl">${escHtml(g.project_name)}</span>
          <span class="cnt">· ${g.tasks.length} задач${g.tasks.length === 1 ? 'а' : g.tasks.length < 5 ? 'и' : ''}</span>
          ${blockedBadge}
        </div>
        <div class="gprog">
          <div class="pbar"><div class="fill" style="width:${Math.round(groupAvg)}%"></div></div>
          <span class="pct">${Math.round(groupAvg)}%</span>
        </div>
        <div class="ghours"><b>${formatRubShort(groupCost)}</b> ₽</div>
        <div class="gpri">${priPips}</div>
      </div>
      ${rowsHtml}
    `;
  }).join('');

  return `
    <div class="md-card">
      <div class="md-card-h">
        <h3>Очередь · по проектам · приоритет CCPM</h3>
        <div class="md-q-summary-chips">
          <span class="chip">Всего <b>${tasks.length}</b></span>
          <span class="chip">Сегодня <b>${tasksToday}</b></span>
          ${tasksOverdue > 0 ? `<span class="chip bad">Просрочено <b>${tasksOverdue}</b></span>` : ''}
        </div>
      </div>
      <div class="md-queue">
        <div class="md-q-head">
          <div>Pri</div>
          <div>Задача</div>
          <div>Ответственный</div>
          <div>Прогресс</div>
          <div>Дедлайн</div>
          <div style="text-align:right">Сумма ₽</div>
          <div>Буфер</div>
        </div>
        ${groupsHtml}
        <div class="md-q-totals">
          <div class="lbl">
            Итого: <b>${tasks.length}</b> задач(и)
            <span class="md-q-totals-sep">·</span>
            <span class="md-q-totals-chip">в работе <b>${inProgressCount}</b></span>
            <span class="md-q-totals-chip">в плане <b>${todoCount}</b></span>
            ${blockedTotalCount > 0 ? `<span class="md-q-totals-chip md-q-totals-chip-bad">блок <b>${blockedTotalCount}</b></span>` : ''}
            <span class="md-q-totals-sep">·</span>
            средний прогресс <b>${Math.round(avgWork)}%</b>
          </div>
          <div class="hrs-sum"><b>${formatRubShort(totalSum)}</b> ₽</div>
        </div>
      </div>
    </div>
  `;
}

function renderQueueRow(t, idx, parentId = 0) {
  const isMine = true; // queue endpoint уже фильтрует по resource_id, все строки — мои
  const fz = t.project_fever_zone;
  const priCls = fz === 'red' ? 'red' : fz === 'yellow' ? 'yellow' : 'green';
  const sdotCls = t.is_on_critical_chain ? 'cc' : t.status;
  const ccTag = t.is_on_critical_chain ? '<span class="cc-tag">CC</span>' : '';
  const workPct = Math.max(0, Math.min(100, Math.round(Number(t.work_pct || 0))));
  const pctCls = workPct >= 100 ? 'done' : workPct === 0 ? 'zero' : '';
  const fillCls = workPct >= 100 ? 'done' : t.is_on_critical_chain ? 'cc' : '';
  const nowCls = t.status === 'in_progress' ? 'now' : '';
  const selectedCls = _selectedTaskId === t.id ? 'selected' : '';

  // Due cell.
  const due = formatDueShort(t.deadline);
  const dueCls = due.cls;

  // Sum cell.
  const cost = Number(t.cost_rub || 0);
  const hrs = t.ruslan_minutes ? (Number(t.ruslan_minutes) / 60).toFixed(1) + ' ч' : '';

  // Buffer cell.
  const bufPct = Math.max(0, Math.min(100, Math.round(Number(t.project_buffer_consumed_pct || 0))));
  const bufFillCls = bufPct > 80 ? 'r' : bufPct > 50 ? 'y' : '';

  // Имя ресурса берём из owner_resource_name (миграция backend my_day queue
  // обогащён JOIN на ccpm.resources). Аватар = первая буква имени.
  const userName = isMine ? 'Я' : '—';
  const rn = t.owner_resource_name || '';
  const userInitials = rn ? rn.charAt(0).toUpperCase() : 'Я';

  return `
    <div class="md-q-row ${nowCls} ${selectedCls}${parentId ? ' under-summary' : ''}" data-task-id="${t.id}" data-project-id="${t.project_id}"${parentId ? ` data-parent-id="${parentId}"` : ''}>
      <div class="pri ${priCls}">${idx}</div>
      <div class="name">
        <span class="sdot ${sdotCls}"></span>
        ${ccTag}
        <span class="txt" data-md-action="open-task" title="${escAttr(t.name)}">${escHtml(t.name)}</span>
      </div>
      <div class="resp me">
        <span class="av">${escHtml(userInitials)}</span>
        <span class="nm">${escHtml(userName)}</span>
      </div>
      <div class="prog">
        <div class="pbar"><div class="fill ${fillCls}" style="width:${workPct}%"></div></div>
        <span class="pct ${pctCls}" contenteditable spellcheck="false" data-md-action="edit-pct" data-task-id="${t.id}">${workPct}%</span>
      </div>
      <div class="due ${dueCls}">${escHtml(due.text)}</div>
      <div class="sum">
        <span><b>${formatRubShort(cost)}</b><span class="cur"> ₽</span></span>
        ${hrs ? `<span class="hh">${escHtml(hrs)}</span>` : ''}
      </div>
      <div><div class="buf"><div class="fill ${bufFillCls}" style="width:${bufPct}%"></div></div></div>
    </div>
  `;
}

// ── Render: Production chart shell ────────────────────────────────────────

function renderProductionSection(production) {
  if (!production.ok) {
    return `
      <div class="md-card">
        <div class="md-card-h"><h3>Производство · 12 недель</h3></div>
        ${renderSectionError('production', production.e)}
      </div>
    `;
  }
  const k = production.v.kpi;

  // Дельта эта неделя vs прошлая, в тыс ₽.
  const deltaThis = k.this_week_actual_rub - k.prev_week_actual_rub;
  const deltaTasks = k.this_week_done_tasks - k.prev_week_done_tasks;
  const deltaThisAbs = Math.abs(deltaThis);
  const deltaThisLabel = deltaThis === 0
    ? '— к прошлой'
    : `${deltaThis > 0 ? '↑ +' : '↓ −'}${formatRubShort(deltaThisAbs)} ₽ к прошлой`;
  const deltaTasksLabel = deltaTasks === 0
    ? '— к прошлой'
    : `${deltaTasks > 0 ? '↑ +' : '↓ −'}${Math.abs(deltaTasks)} к прошлой`;
  // Среднее за 12 недель (для подписи avg-линии).
  const avg12 = Math.round(k.total_actual_rub_12w / 12);

  return `
    <div class="md-card">
      <div class="md-card-h"><h3>Производство · 12 недель</h3></div>
      <div class="md-prod">
        <div class="md-prod-kpi">
          <div class="kp">
            <div class="lbl">Эта неделя · сделано</div>
            <div class="v good">${formatRubShort(k.this_week_actual_rub)} <small>₽</small></div>
            <div class="delta ${deltaThis > 0 ? 'up' : deltaThis < 0 ? 'down' : ''}">${escHtml(deltaThisLabel)}</div>
          </div>
          <div class="kp">
            <div class="lbl">12 нед · оборот</div>
            <div class="v">${formatRubShort(k.total_actual_rub_12w)} <small>₽</small></div>
            <div class="delta">в среднем ${formatRubShort(avg12)} ₽/нед</div>
          </div>
          <div class="kp">
            <div class="lbl">Утилизация (12 нед)</div>
            <div class="v">${k.avg_utilization_pct != null ? clampPctDisplay(k.avg_utilization_pct) : '—'}</div>
          </div>
          <div class="kp">
            <div class="lbl">Задач закрыто</div>
            <div class="v">${k.tasks_done_12w}</div>
            <div class="delta ${deltaTasks > 0 ? 'up' : deltaTasks < 0 ? 'down' : ''}">${escHtml(deltaTasksLabel)}</div>
          </div>
        </div>
        <svg class="md-prod-chart" id="md-prod-chart" viewBox="0 0 720 200" preserveAspectRatio="none"></svg>
        <div class="md-prod-legend">
          <span class="lg"><span class="sw bar-plan"></span>План</span>
          <span class="lg"><span class="sw bar-fact"></span>Факт</span>
          <span class="lg"><span class="sw bar-over"></span>Сверх плана</span>
          <span class="lg"><span class="sw line"></span>Тренд по факту</span>
          <span class="lg"><span class="sw avg"></span>Среднее: ${formatRubShort(avg12)} ₽/нед</span>
        </div>
      </div>
    </div>
  `;
}

function drawProductionChart(data) {
  const svg = document.getElementById('md-prod-chart');
  if (!svg) return;

  const weeks = data.weeks || [];
  if (!weeks.length) return;

  const W = 720;
  const H = 200;
  const padL = 36;
  const padR = 12;
  const padT = 12;
  const padB = 28;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;

  // Y-scale (max across plan + fact).
  const maxVal = Math.max(
    1,
    ...weeks.map((w) => Math.max(Number(w.planned_rub || 0), Number(w.actual_rub || 0))),
  );
  const yScale = (v) => padT + innerH - (Number(v || 0) / maxVal) * innerH;

  const bw = innerW / weeks.length;
  const planW = bw * 0.28;
  const factW = bw * 0.38;

  const factPts = [];
  let bars = '';
  let labels = '';
  let valueLabels = '';
  weeks.forEach((w, i) => {
    const x = padL + i * bw;
    const planY = yScale(w.planned_rub);
    const factY = yScale(w.actual_rub);
    const groundY = padT + innerH;
    const factVal = Number(w.actual_rub || 0);
    const planVal = Number(w.planned_rub || 0);

    // Plan ghost (если есть план).
    if (planVal > 0) {
      bars += `<rect x="${x + bw * 0.18}" y="${planY}" width="${planW}" height="${groundY - planY}"
                fill="rgba(20,184,166,0.18)" stroke="rgba(20,184,166,0.45)" stroke-width="1"/>`;
    }
    // Fact bar (teal).
    if (factVal > 0) {
      bars += `<rect x="${x + bw * 0.46}" y="${factY}" width="${factW}" height="${groundY - factY}"
                fill="var(--teal)"/>`;
      // Over-plan overlay (warn, прозрачный — слегка обозначить).
      if (factVal > planVal && planVal > 0) {
        const overBot = yScale(planVal);
        bars += `<rect x="${x + bw * 0.46}" y="${factY}" width="${factW}" height="${overBot - factY}"
                  fill="var(--fever-yellow)" opacity="0.4"/>`;
      }
      // Числовая подпись над столбцом (формат «N» в тыс ₽ — если значение ≥1000).
      const lblFact = formatRubShort(factVal);
      valueLabels += `<text x="${x + bw * 0.46 + factW / 2}" y="${factY - 4}"
                      text-anchor="middle" font-size="10" font-weight="600"
                      fill="var(--text-secondary)">${lblFact}</text>`;
      factPts.push([x + bw * 0.46 + factW / 2, factY, true]);
    } else {
      factPts.push([x + bw * 0.46 + factW / 2, groundY, false]);
    }

    // X-axis label: номер недели + дата начала. ISO week number — `getISOWeekNumber`.
    const d = new Date(w.wk_start);
    const wkNum = getISOWeekNumber(d);
    const endD = new Date(d.getTime() + 6 * 86400_000);
    const lbl1 = `W${wkNum}`;
    const lbl2 = `${d.getDate()}-${endD.getDate()}.${String(d.getMonth() + 1).padStart(2, '0')}`;
    labels += `<text x="${x + bw / 2}" y="${H - 14}" text-anchor="middle"
                font-size="10" fill="var(--text-tertiary)"
                font-weight="600">${lbl1}</text>`;
    labels += `<text x="${x + bw / 2}" y="${H - 4}" text-anchor="middle"
                font-size="9" fill="var(--text-tertiary)">${lbl2}</text>`;
  });

  // Trend polyline — пропускает пустые (factVal=0) недели через path M/L segments.
  const segs = [];
  let cur = null;
  factPts.forEach(([x, y, present]) => {
    if (!present) {
      if (cur && cur.length >= 2) segs.push(cur);
      cur = null;
      return;
    }
    if (cur == null) cur = [];
    cur.push([x, y]);
  });
  if (cur && cur.length >= 2) segs.push(cur);

  const trendD = segs.map((seg) => seg.map((pt, i) => (i === 0 ? `M${pt[0]},${pt[1]}` : `L${pt[0]},${pt[1]}`)).join(' ')).join(' ');
  const trendDots = factPts
    .filter(([, , present]) => present)
    .map(([x, y]) => `<circle cx="${x}" cy="${y}" r="2.5" fill="var(--teal-bright)"/>`)
    .join('');
  const trend = trendD
    ? `<path d="${trendD}" fill="none" stroke="var(--teal-bright)" stroke-width="1.5" stroke-linejoin="round"/>${trendDots}`
    : '';

  // Avg line — среднее по факту за 12 недель.
  const avg = weeks.reduce((s, w) => s + Number(w.actual_rub || 0), 0) / weeks.length;
  const avgY = yScale(avg);
  const avgLine = `<line x1="${padL}" y1="${avgY}" x2="${padL + innerW}" y2="${avgY}"
                    stroke="var(--text-tertiary)" stroke-dasharray="4,4" stroke-width="1"/>
                   <text x="${padL + innerW + 2}" y="${avgY + 3}" font-size="10"
                    fill="var(--text-tertiary)">ср ${formatRubShort(avg)}</text>`;

  svg.innerHTML = `${bars}${avgLine}${trend}${valueLabels}${labels}`;
}

// ISO 8601 week number (понедельник — первый день недели).
function getISOWeekNumber(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

// ── Render: Capacity heatmap ──────────────────────────────────────────────

function renderCapacitySection(workload) {
  if (!workload.ok) {
    return `
      <div class="md-side-section">
        <h3>Загрузка · 2 недели</h3>
        ${renderSectionError('capacity', workload.e)}
      </div>
    `;
  }
  const allRows = workload.v;
  if (!allRows.length) {
    return `
      <div class="md-side-section">
        <h3>Загрузка · 2 недели</h3>
        <div class="md-loading">Нет данных</div>
      </div>
    `;
  }

  const todayIso = new Date().toISOString().slice(0, 10);

  // Workload приходит начиная с TODAY. Чтобы сетка была от понедельника:
  // 1) Найти понедельник текущей недели.
  // 2) Построить map work_date → row для O(1) lookup.
  // 3) Заполнить 2 ряда × 7 дней, начиная с понедельника.
  const byDate = new Map(allRows.map((r) => [r.work_date, r]));
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const monday = new Date(today.getTime() - ((today.getDay() + 6) % 7) * 86400_000);

  // Header row: Пн Вт Ср Чт Пт Сб Вс (weekday-1 в нашей константе = индекс).
  const weekdayLabels = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
  let cellsHtml = '<div class="rowlabel"></div>';
  for (let i = 0; i < 7; i++) {
    const day = new Date(monday.getTime() + i * 86400_000);
    const dayIso = day.toISOString().slice(0, 10);
    const isToday = dayIso === todayIso;
    cellsHtml += `<div class="collabel ${isToday ? 'today' : ''}">${weekdayLabels[i]}</div>`;
  }

  // 2 ряда — нед.1 (текущая), нед.2 (следующая).
  let weekPlanned = [0, 0];
  let weekCapacity = [0, 0];
  for (let w = 0; w < 2; w++) {
    cellsHtml += `<div class="rowlabel">${w === 0 ? 'нед1' : 'нед2'}</div>`;
    for (let d = 0; d < 7; d++) {
      const day = new Date(monday.getTime() + (w * 7 + d) * 86400_000);
      const dayIso = day.toISOString().slice(0, 10);
      const row = byDate.get(dayIso);
      if (!row) {
        cellsHtml += '<div class="cell empty" data-h="—"></div>';
        continue;
      }
      const planned = Number(row.planned_minutes || 0);
      const capacity = Number(row.capacity_minutes || 0);
      weekPlanned[w] += planned;
      weekCapacity[w] += capacity;
      const isToday = dayIso === todayIso;
      const util = capacity > 0 ? planned / capacity : 0;
      let cls = 'cell';
      if (util === 0) cls += ' empty';
      else if (util < 0.3) cls += ' l1';
      else if (util < 0.6) cls += ' l2';
      else if (util < 0.85) cls += ' l3';
      else if (util <= 1.0) cls += ' l4';
      else if (util <= 1.3) cls += ' over';
      else cls += ' crit';
      if (isToday) cls += ' today';
      const hh = (planned / 60).toFixed(1);
      cellsHtml += `<div class="${cls}" data-h="${hh}" title="${formatDateShort(dayIso)}: ${hh}ч / ${(capacity / 60).toFixed(1)}ч"></div>`;
    }
  }

  const wkPlannedH = (weekPlanned[0] / 60).toFixed(1);
  const wkCapH = (weekCapacity[0] / 60).toFixed(1);
  const utilThisWeek = weekCapacity[0] > 0 ? Math.round((weekPlanned[0] / weekCapacity[0]) * 100) : 0;

  return `
    <div class="md-side-section">
      <h3>Загрузка · 2 недели <span class="sub">часов / день</span></h3>
      <div class="md-cap-grid">${cellsHtml}</div>
      <div class="md-cap-legend">
        <span class="lg"><span class="sw" style="background:rgba(20,184,166,0.14)"></span>0-4ч</span>
        <span class="lg"><span class="sw" style="background:rgba(20,184,166,0.30)"></span>4-8ч</span>
        <span class="lg"><span class="sw" style="background:rgba(20,184,166,0.70)"></span>8-10ч</span>
        <span class="lg"><span class="sw" style="background:rgba(210,153,34,0.55)"></span>&gt;10</span>
      </div>
      <div class="md-cap-summary">
        <span title="План: сумма planned_minutes твоих задач по дням этой недели. Capacity: сумма net_project_minutes из ccpm.daily_capacity за 7 дней с учётом recurring блоков и выходных.">Эта неделя: <b>${wkPlannedH}</b> / ${wkCapH} ч</span>
        <span title="План / Capacity. >100% = недели не хватает на план; проверь оценки задач или разгрузи recurring блоки.">Утилизация: <b ${utilThisWeek > 100 ? 'style="color:var(--fever-yellow)"' : ''}>${clampPctDisplay(utilThisWeek)}</b></span>
      </div>
    </div>
  `;
}

// ── Render: Profile section (миграция 048: rate + skills + role) ──────────

function renderProfileSection(user, hero) {
  const cr = hero.ok ? hero.v.current_resource : null;
  if (!user) return '';
  const initials = (cr?.name || user.full_name || 'Я')
    .split(/\s+/)
    .map((s) => s[0])
    .slice(0, 1)
    .join('')
    .toUpperCase();
  const roleLabel = {
    owner: 'Собственник', admin: 'Админ', manager: 'Менеджер', user: 'Пользователь',
  }[user.role] || user.role;
  const rate = cr?.rate_rub_per_hour ? `${formatRubShort(Number(cr.rate_rub_per_hour))} ₽/ч` : null;
  const skills = Array.isArray(cr?.skills) ? cr.skills : [];
  const visibleSkills = skills.slice(0, 5);
  const moreSkills = skills.length > 5 ? skills.length - 5 : 0;

  return `
    <div class="md-side-section md-profile-section">
      <h3>Профиль</h3>
      <div class="md-profile">
        <div class="md-profile-av">${escHtml(initials)}</div>
        <div class="md-profile-body">
          <div class="md-profile-name">${escHtml(user.full_name || user.login)}</div>
          <div class="md-profile-role">
            ${escHtml(roleLabel)}${rate ? ` · ставка ${escHtml(rate)}` : ''}
          </div>
        </div>
      </div>
      ${visibleSkills.length ? `
        <div class="md-profile-skills">
          ${visibleSkills.map((s) => `<span class="skill-tag">${escHtml(s)}</span>`).join('')}
          ${moreSkills ? `<span class="skill-tag muted">+${moreSkills}</span>` : ''}
        </div>
      ` : ''}
    </div>
  `;
}

// ── Render: Fever mini ────────────────────────────────────────────────────

function renderFeverMiniSection(hero) {
  if (!hero.ok) return '';
  const projects = hero.v.my_projects_fever || [];
  if (!projects.length) {
    return `
      <div class="md-side-section">
        <h3>Fever · мои проекты <span class="sub">CC% × Buffer%</span></h3>
        <div class="md-loading">Нет CC-проектов</div>
      </div>
    `;
  }
  const legend = projects.map((p) => {
    const cls = p.fever_zone === 'red' ? 'red' : p.fever_zone === 'yellow' ? 'yellow' : 'green';
    const cc = p.progress_pct != null ? Math.round(Number(p.progress_pct)) : '—';
    const buf = p.buffer_consumed_pct != null ? Math.round(Number(p.buffer_consumed_pct)) : '—';
    return `
      <div class="proj">
        <span class="dot ${cls}"></span>
        <span class="nm" title="${escAttr(p.project_name)}">${escHtml(p.project_name)}</span>
        <span class="meta">CC ${cc}% · Buf ${buf}%</span>
      </div>
    `;
  }).join('');
  return `
    <div class="md-side-section">
      <h3>Fever · мои проекты <span class="sub">CC% × Buffer%</span></h3>
      <svg class="md-fever-svg" id="md-fever-svg" viewBox="0 0 320 200" aria-label="Fever chart"></svg>
      <div class="md-fever-legend">${legend}</div>
    </div>
  `;
}

function drawFeverMini(projects) {
  const svg = document.getElementById('md-fever-svg');
  if (!svg) return;
  const W = 320;
  const H = 200;
  // Coordinate system: X = CC progress %, Y = Buffer consumed %.
  // SVG Y растёт вниз, мы инвертируем (0% buffer внизу, 100% сверху —
  // стандартный fever chart 2D).
  const padL = 26;
  const padR = 10;
  const padT = 16;
  const padB = 24;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;

  // Helper: convert (cc%, buf%) → SVG (x, y).
  const xOf = (cc) => padL + (Math.max(0, Math.min(100, cc)) / 100) * innerW;
  const yOf = (buf) => padT + (1 - Math.max(0, Math.min(100, buf)) / 100) * innerH;

  // РАДИАЛЬНЫЕ fever-зоны — точно по backend-формуле fever_zone
  // (projects.py:135-139): ratio = buffer_consumed_pct / progress_pct.
  //   ratio < 1/3        → green
  //   1/3 ≤ ratio < 2/3  → yellow
  //   ratio ≥ 2/3        → red
  // Геометрически это ЛУЧИ из точки (0,0):
  //   луч buf = progress/3   — граница green/yellow
  //   луч buf = 2·progress/3 — граница yellow/red
  // Зоны = секторы между лучами. При progress=100 границы на buf=33.3 и 66.7.
  // Так фон зон ТОЧНО совпадает с цветом маркеров (fever_zone из БД).
  const Y1 = 100 / 3;   // buf на луче green/yellow при cc=100
  const Y2 = 200 / 3;   // buf на луче yellow/red при cc=100

  // GREEN: сектор ниже луча buf=cc/3 — (0,0)→(100,0)→(100, Y1).
  const greenPath = `M ${xOf(0)} ${yOf(0)} L ${xOf(100)} ${yOf(0)} L ${xOf(100)} ${yOf(Y1)} Z`;
  // YELLOW: сектор между лучами — (0,0)→(100,Y1)→(100,Y2).
  const yellowPath = `M ${xOf(0)} ${yOf(0)} L ${xOf(100)} ${yOf(Y1)} L ${xOf(100)} ${yOf(Y2)} Z`;
  // RED: сектор выше луча buf=2cc/3 — (0,0)→(100,Y2)→(100,100)→(0,100).
  const redPath = `M ${xOf(0)} ${yOf(0)} L ${xOf(100)} ${yOf(Y2)} L ${xOf(100)} ${yOf(100)} L ${xOf(0)} ${yOf(100)} Z`;

  // Диагональная штриховка зон (SVG <pattern>) — как в дизайн-мокапе.
  // Каждая зона: полупрозрачная заливка + диагональные линии своего цвета.
  const defs = `
    <defs>
      <pattern id="fv-hatch-red" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
        <rect width="6" height="6" fill="rgba(183,28,28,0.22)"/>
        <line x1="0" y1="0" x2="0" y2="6" stroke="rgba(255,120,120,0.45)" stroke-width="1.5"/>
      </pattern>
      <pattern id="fv-hatch-yellow" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
        <rect width="6" height="6" fill="rgba(210,153,34,0.20)"/>
        <line x1="0" y1="0" x2="0" y2="6" stroke="rgba(230,180,70,0.45)" stroke-width="1.5"/>
      </pattern>
      <pattern id="fv-hatch-green" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
        <rect width="6" height="6" fill="rgba(20,184,166,0.18)"/>
        <line x1="0" y1="0" x2="0" y2="6" stroke="rgba(45,212,191,0.40)" stroke-width="1.5"/>
      </pattern>
    </defs>
  `;

  const zones = `
    <path d="${redPath}"    fill="url(#fv-hatch-red)"    stroke="none"/>
    <path d="${yellowPath}" fill="url(#fv-hatch-yellow)" stroke="none"/>
    <path d="${greenPath}"  fill="url(#fv-hatch-green)"  stroke="none"/>
  `;

  // Сетка 0/50/100 + пунктирные лучи-границы зон (из 0,0).
  const grid = `
    <line x1="${xOf(50)}" y1="${padT}" x2="${xOf(50)}" y2="${padT + innerH}" stroke="rgba(255,255,255,0.06)" stroke-width="1"/>
    <line x1="${padL}" y1="${yOf(50)}" x2="${padL + innerW}" y2="${yOf(50)}" stroke="rgba(255,255,255,0.06)" stroke-width="1"/>
    <line x1="${xOf(0)}" y1="${yOf(0)}" x2="${xOf(100)}" y2="${yOf(Y1)}" stroke="rgba(255,255,255,0.18)" stroke-width="1" stroke-dasharray="3,3"/>
    <line x1="${xOf(0)}" y1="${yOf(0)}" x2="${xOf(100)}" y2="${yOf(Y2)}" stroke="rgba(255,255,255,0.18)" stroke-width="1" stroke-dasharray="3,3"/>
  `;

  // Оси (frame).
  const frame = `
    <line x1="${padL}" y1="${padT}" x2="${padL}" y2="${padT + innerH}" stroke="var(--border-default)" stroke-width="1"/>
    <line x1="${padL}" y1="${padT + innerH}" x2="${padL + innerW}" y2="${padT + innerH}" stroke="var(--border-default)" stroke-width="1"/>
  `;

  // Подписи осей. Без rotated-текста «Buf %» (он накладывался на «100%»).
  // Слева — числа, снизу — числа + «CC %» по центру.
  const labels = `
    <text x="${padL - 6}" y="${yOf(100) + 3}" font-size="9" fill="var(--text-tertiary)" text-anchor="end">100</text>
    <text x="${padL - 6}" y="${yOf(50) + 3}"  font-size="9" fill="var(--text-tertiary)" text-anchor="end">50</text>
    <text x="${padL - 6}" y="${yOf(0) + 3}"   font-size="9" fill="var(--text-tertiary)" text-anchor="end">0</text>
    <text x="${xOf(0)}"   y="${padT + innerH + 14}" font-size="9" fill="var(--text-tertiary)" text-anchor="start">0%</text>
    <text x="${xOf(50)}"  y="${padT + innerH + 14}" font-size="9" fill="var(--text-tertiary)" text-anchor="middle">50%</text>
    <text x="${xOf(100)}" y="${padT + innerH + 14}" font-size="9" fill="var(--text-tertiary)" text-anchor="end">100%</text>
    <text x="${padL + innerW / 2}" y="${H - 4}" font-size="9" fill="var(--text-tertiary)" text-anchor="middle">X — CC % выполнено · Y — Buffer % съедено</text>
  `;

  // Maркеры проектов.
  const pts = projects.map((p) => {
    const cc = Number(p.progress_pct || 0);
    const buf = Number(p.buffer_consumed_pct || 0);
    const cx = xOf(cc);
    const cy = yOf(buf);
    const color = p.fever_zone === 'red' ? 'var(--fever-red)'
      : p.fever_zone === 'yellow' ? 'var(--fever-yellow)'
      : 'var(--teal-bright)';
    return `<circle cx="${cx}" cy="${cy}" r="5" fill="${color}" stroke="var(--bg-canvas)" stroke-width="1.5">
              <title>${p.project_name}: CC ${Math.round(cc)}%, Buf ${Math.round(buf)}%</title>
            </circle>`;
  }).join('');

  svg.innerHTML = defs + zones + grid + frame + labels + pts;
}

// ── Render: Mini Gantt ────────────────────────────────────────────────────

function renderMiniGanttSection(queue) {
  if (!queue.ok || !queue.v.length) return '';

  // Группируем задачи с датами по проекту. Лимит — 12 строк (по дизайну,
  // высота 230px = 12 × 22px - заголовок).
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = new Date(today.getTime() - 2 * 86400_000);
  const end = new Date(today.getTime() + 12 * 86400_000);
  const visible = queue.v
    .filter((t) => t.planned_start_date && t.planned_end_date)
    .filter((t) => {
      const ts = new Date(t.planned_start_date);
      const te = new Date(t.planned_end_date);
      return te >= start && ts <= end;
    });

  const groups = new Map();
  for (const t of visible) {
    if (!groups.has(t.project_id)) {
      groups.set(t.project_id, {
        project_id: t.project_id,
        project_name: t.project_name,
        tasks: [],
      });
    }
    groups.get(t.project_id).tasks.push(t);
  }

  // Сборка строк: для каждой группы header + строки задач. Лимит 12 строк.
  const tableRows = [];
  const allBars = [];  // {row_index, task} — для SVG
  let rowIdx = 0;
  for (const g of groups.values()) {
    if (rowIdx >= 12) break;
    // Project header.
    const gDays = Math.round((Math.max(...g.tasks.map((t) => new Date(t.planned_end_date).getTime()))
      - Math.min(...g.tasks.map((t) => new Date(t.planned_start_date).getTime())))
      / 86400_000) + 1;
    const gAvg = Math.round(g.tasks.reduce((s, t) => s + Number(t.work_pct || 0), 0) / g.tasks.length);
    tableRows.push({
      kind: 'group',
      project_id: g.project_id,
      label: g.project_name,
      days: gDays,
      pct: gAvg,
    });
    rowIdx += 1;
    // Task rows in this group.
    for (const t of g.tasks) {
      if (rowIdx >= 12) break;
      const tDays = Math.round((new Date(t.planned_end_date) - new Date(t.planned_start_date)) / 86400_000) + 1;
      tableRows.push({
        kind: 'task',
        project_id: t.project_id,
        label: t.name,
        days: tDays,
        pct: Math.round(Number(t.work_pct || 0)),
      });
      allBars.push({ rowIndex: rowIdx, task: t });
      rowIdx += 1;
    }
  }

  const rowH = 22;
  const headerH = 28;
  const tableHtml = tableRows.map((r) => {
    if (r.kind === 'group') {
      return `
        <div class="md-gantt-row group" data-project-id="${r.project_id}">
          <span class="nm">${escHtml(r.label)}</span>
          <span class="dn">${r.days}d</span>
          <span class="pct">${r.pct}%</span>
        </div>
      `;
    }
    return `
      <div class="md-gantt-row task">
        <span class="nm" title="${escAttr(r.label)}">${escHtml(r.label)}</span>
        <span class="dn">${r.days}</span>
        <span class="pct">${r.pct}%</span>
      </div>
    `;
  }).join('');

  const svgH = headerH + rowIdx * rowH;
  return `
    <div class="md-card md-gantt-card">
      <div class="md-card-h"><h3>Мой Гантт · 14 дней</h3></div>
      <div class="md-gantt-wrap" data-svg-h="${svgH}">
        <div class="md-gantt-table">
          <div class="md-gantt-table-head">
            <span class="nm">Задача · Проект</span>
            <span class="dn">Дн</span>
            <span class="pct">%</span>
          </div>
          ${tableHtml}
        </div>
        <svg class="md-gantt-svg" id="md-gantt-svg"
             viewBox="0 0 1000 ${svgH}" preserveAspectRatio="xMinYMin"
             style="height:${svgH}px"></svg>
      </div>
    </div>
  `;
}

function drawMiniGantt(tasks) {
  const svg = document.getElementById('md-gantt-svg');
  if (!svg) return;

  // Размеры берём из data-атрибута wrap (заданы при HTML-рендере).
  const wrap = svg.parentElement;
  const H = Number(wrap?.dataset?.svgH || 230);

  // Окно: today−2 ... today+11 (14 дней).
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = new Date(today.getTime() - 2 * 86400_000);
  const end = new Date(today.getTime() + 12 * 86400_000);
  const days = 14;
  const W = 1000;
  const padL = 4;
  const padR = 4;
  const headerH = 28;
  const rowH = 22;
  const dayW = (W - padL - padR) / days;

  // Группируем задачи как и в renderMiniGanttSection, чтобы row-index синхронен с таблицей.
  const visible = tasks
    .filter((t) => t.planned_start_date && t.planned_end_date)
    .filter((t) => {
      const ts = new Date(t.planned_start_date);
      const te = new Date(t.planned_end_date);
      return te >= start && ts <= end;
    });
  const groupsMap = new Map();
  for (const t of visible) {
    if (!groupsMap.has(t.project_id)) {
      groupsMap.set(t.project_id, []);
    }
    groupsMap.get(t.project_id).push(t);
  }

  // Day grid + header (weekday + дата).
  const WEEKDAY_LBL = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];
  let grid = '';
  let header = '';
  for (let i = 0; i < days; i++) {
    const d = new Date(start.getTime() + i * 86400_000);
    const x = padL + i * dayW;
    const isWeekend = d.getDay() === 0 || d.getDay() === 6;
    const isToday = d.getTime() === today.getTime();
    grid += `<rect x="${x}" y="${headerH}" width="${dayW}" height="${H - headerH}"
              fill="${isWeekend ? 'rgba(0,0,0,0.18)' : 'transparent'}"
              stroke="var(--border-muted)" stroke-width="0.5"/>`;
    const wdLbl = WEEKDAY_LBL[d.getDay()];
    // Плотный формат: только день месяца (с zero-pad); при смене месяца — добавляем номер месяца.
    const dayNum = String(d.getDate()).padStart(2, '0');
    const dateLbl = d.getDate() === 1 || i === 0
      ? `${dayNum}.${String(d.getMonth() + 1).padStart(2, '0')}`
      : dayNum;
    header += `<text x="${x + dayW / 2}" y="12" font-size="9"
                text-anchor="middle"
                fill="${isToday ? 'var(--teal-bright)' : 'var(--text-tertiary)'}"
                ${isToday ? 'font-weight="600"' : ''}>${wdLbl}</text>`;
    header += `<text x="${x + dayW / 2}" y="22" font-size="10"
                text-anchor="middle"
                fill="${isToday ? 'var(--teal-bright)' : 'var(--text-tertiary)'}"
                ${isToday ? 'font-weight="600"' : ''}>${dateLbl}</text>`;
  }
  const todayX = padL + ((today.getTime() - start.getTime()) / 86400_000) * dayW;
  const todayLine = `<line x1="${todayX}" y1="${headerH}" x2="${todayX}" y2="${H}"
                      stroke="var(--teal-bright)" stroke-width="1.5"
                      stroke-dasharray="3,3"/>`;

  // Bars — row-index согласован с tableRows (group header занимает строку, потом task'и).
  let rowIdx = 0;
  let bars = '';
  for (const tasks of groupsMap.values()) {
    if (rowIdx >= 12) break;
    // Group row (пустая в SVG, только фон).
    bars += `<rect x="0" y="${headerH + rowIdx * rowH}" width="${W}" height="${rowH}"
              fill="rgba(96,125,139,0.08)"/>`;
    rowIdx += 1;
    for (const t of tasks) {
      if (rowIdx >= 12) break;
      const ts = new Date(t.planned_start_date);
      const te = new Date(t.planned_end_date);
      const clamped_s = Math.max(ts.getTime(), start.getTime());
      const clamped_e = Math.min(te.getTime(), end.getTime());
      const x = padL + ((clamped_s - start.getTime()) / 86400_000) * dayW;
      const w = Math.max(2, ((clamped_e - clamped_s) / 86400_000 + 1) * dayW - 1);
      const y = headerH + rowIdx * rowH + 4;
      const h = rowH - 8;
      const fill = t.status === 'done' ? 'var(--teal-deep)'
        : t.is_on_critical_chain ? 'var(--critical-chain)'
        : t.status === 'in_progress' ? 'var(--teal)'
        : 'rgba(20,184,166,0.30)';
      const stroke = t.status === 'blocked' ? 'var(--blocked)' : 'var(--teal-deep)';
      bars += `<g style="cursor:pointer" data-md-task-id="${t.id}" data-project-id="${t.project_id}">
                <rect x="${x}" y="${y}" width="${w}" height="${h}" rx="2"
                      fill="${fill}" stroke="${stroke}" stroke-width="1">
                  <title>${t.name}</title>
                </rect>
              </g>`;
      rowIdx += 1;
    }
  }

  svg.innerHTML = grid + header + bars + todayLine;
}

// ── Render: Notifications feed (миграция 050, MVP C1) ───────────────────

function renderNotificationsSection(notifications) {
  if (!notifications.ok) {
    return `
      <div class="md-side-section">
        <h3>События</h3>
        ${renderSectionError('notifications', notifications.e)}
      </div>
    `;
  }
  const items = notifications.v || [];
  if (!items.length) {
    return `
      <div class="md-side-section">
        <h3>События</h3>
        <div class="md-loading">Пока нет упоминаний и комментариев</div>
      </div>
    `;
  }
  const itemsHtml = items.map((n) => {
    const author = n.author_full_name || n.author_login || 'system';
    const initials = author.split(/\s+/).map(s => s[0]).slice(0, 1).join('').toUpperCase() || '?';
    const verb = n.kind === 'mention' ? 'упомянул тебя в задаче' : 'прокомментировал твою задачу';
    const body = n.comment_body ? `: «${escHtml(String(n.comment_body).slice(0, 100))}${n.comment_body.length > 100 ? '…' : ''}»` : '';
    const taskName = n.task_name ? `«${escHtml(n.task_name)}»` : `#${n.task_id}`;
    const unreadCls = n.read_at ? '' : 'unread';
    return `
      <div class="md-feed-item md-notif ${unreadCls}" data-notif-id="${n.id}" data-task-id="${n.task_id}" data-project-id="${n.project_id || ''}">
        <span class="ic notif-av">${escHtml(initials)}</span>
        <div class="body">
          <b>${escHtml(author)}</b> ${escHtml(verb)} ${taskName}${body}
          <span class="when">${formatRelative(n.created_at)}</span>
        </div>
      </div>
    `;
  }).join('');
  const unreadCount = items.filter(n => !n.read_at).length;
  return `
    <div class="md-side-section">
      <h3>События ${unreadCount > 0 ? `<span class="sub unread">${unreadCount} новых</span>` : `<span class="sub">${items.length}</span>`}</h3>
      <div class="md-feed">${itemsHtml}</div>
      ${unreadCount > 0 ? `<button class="md-mark-all-read" data-md-action="mark-all-read">Отметить всё прочитанным</button>` : ''}
    </div>
  `;
}

// ── Render: Activity feed (audit log, технический) ───────────────────────

function renderActivitySection(activity) {
  if (!activity.ok) {
    return `
      <div class="md-side-section">
        <h3>Аудит-лог</h3>
        ${renderSectionError('activity', activity.e)}
      </div>
    `;
  }
  const items = activity.v.slice(0, 15);
  if (!items.length) {
    return `
      <div class="md-side-section">
        <h3>Аудит-лог</h3>
        <div class="md-loading">Пока пусто</div>
      </div>
    `;
  }
  const itemsHtml = items.map((a) => {
    const icon = iconForAction(a.action);
    const text = describeAction(a);
    return `
      <div class="md-feed-item" data-entity-type="${escAttr(a.entity_type)}" data-entity-id="${a.entity_id}">
        <span class="ic ${icon.cls}">${icon.glyph}</span>
        <div class="body">
          ${text}
          <span class="when">${formatRelative(a.timestamp)} · ${escHtml(a.user_id || '—')}</span>
        </div>
      </div>
    `;
  }).join('');
  return `
    <div class="md-side-section">
      <h3>Аудит-лог <span class="sub">${items.length}</span></h3>
      <div class="md-feed">${itemsHtml}</div>
    </div>
  `;
}

function iconForAction(action) {
  if (!action) return { cls: '', glyph: '·' };
  if (action.endsWith('.complete') || action.endsWith('.done')) return { cls: 'good', glyph: '✓' };
  if (action.endsWith('.block')) return { cls: 'bad', glyph: '!' };
  if (action.endsWith('.cancel')) return { cls: 'bad', glyph: '✕' };
  if (action.endsWith('.start')) return { cls: 'good', glyph: '▶' };
  if (action.endsWith('.create')) return { cls: 'good', glyph: '+' };
  if (action.endsWith('.update')) return { cls: '', glyph: '~' };
  return { cls: '', glyph: '·' };
}

function describeAction(a) {
  const verbMap = {
    'task.start': 'начал задачу',
    'task.complete': 'завершил задачу',
    'task.block': 'заблокировал задачу',
    'task.unblock': 'разблокировал задачу',
    'task.update': 'обновил задачу',
    'task.create': 'создал задачу',
    'task.cancel': 'отменил задачу',
    'project.update': 'обновил проект',
    'project.complete': 'завершил проект',
    'project.cancel': 'отменил проект',
    'project.create': 'создал проект',
  };
  const verb = verbMap[a.action] || a.action;
  return `<b>${escHtml(verb)}</b> #${a.entity_id || '—'}`;
}

// ── Section error helper ───────────────────────────────────────────────────

function renderSectionError(name, err) {
  const msg = err?.detail || err?.message || 'ошибка';
  return `<div class="md-section-error">[${escHtml(name)}] ${escHtml(String(msg))}</div>`;
}

// ── Handlers ───────────────────────────────────────────────────────────────

function attachHeroHandlers(root) {
  // Inline-edit progress в hero workflow.
  const pctEl = root.querySelector('.md-hero-progress .pct-edit');
  if (pctEl) {
    let originalPct = pctEl.textContent;
    pctEl.addEventListener('focus', () => { originalPct = pctEl.textContent; });
    pctEl.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); pctEl.blur(); }
      if (e.key === 'Escape') { pctEl.textContent = originalPct; pctEl.blur(); }
    });
    pctEl.addEventListener('blur', async () => {
      const raw = pctEl.textContent.replace(/[^\d]/g, '');
      const val = Math.max(0, Math.min(100, parseInt(raw, 10) || 0));
      pctEl.textContent = val + '%';
      if (pctEl.textContent === originalPct) return;
      const taskId = Number(pctEl.dataset.taskId);
      try {
        await api.patchTask(taskId, { work_pct: val });
        reload(root);
      } catch (err) {
        pctEl.textContent = originalPct;
        alert(`Не удалось сохранить: ${err.detail || err.message}`);
      }
    });
  }

  // Ctrl/Cmd+Enter в textarea отправляет коммент.
  const commentTa = root.querySelector('.md-hero-comment-input');
  if (commentTa) {
    commentTa.addEventListener('keydown', (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        const btn = root.querySelector('[data-md-action="hero-send-comment"]');
        if (btn) btn.click();
      }
    });
  }

  root.querySelectorAll('[data-md-action]').forEach((btn) => {
    if (btn.classList.contains('pct')) return;  // queue inline-edit handled separately
    if (btn.classList.contains('pct-edit')) return;  // hero inline-edit handled выше
    if (btn.dataset.mdAction === 'open-task') return;
    if (btn.dataset.mdAction === 'toggle-group') return;
    if (btn.dataset.mdAction === 'toggle-subgroup') return;
    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();
      const action = btn.dataset.mdAction;
      const taskId = Number(btn.dataset.taskId);
      if (!taskId) return;

      try {
        if (action === 'session-start') {
          await api.myDay.sessionStart(taskId);
          reload(root);
        } else if (action === 'session-pause') {
          await api.myDay.sessionPause(taskId);
          reload(root);
        } else if (action === 'session-resume') {
          await api.myDay.sessionResume(taskId);
          reload(root);
        } else if (action === 'session-stop') {
          // При «Стоп» спрашиваем фактическое время — предзаполняем
          // посчитанным таймером (HH:MM:SS из .md-timer-clock).
          const clockEl = root.querySelector('.md-timer-clock');
          let suggested = 0;
          if (clockEl) {
            const parts = clockEl.textContent.trim().split(':').map(Number);
            if (parts.length === 3) suggested = parts[0] * 60 + parts[1] + (parts[2] >= 30 ? 1 : 0);
            else if (parts.length === 2) suggested = parts[0] * 60 + parts[1];
          }
          const ans = prompt(
            'Сколько минут фактически потрачено на задачу?\n' +
            '(0 — если к задаче не приступал; макс 1440)',
            String(suggested),
          );
          if (ans == null) return;
          const mins = Math.max(0, Math.min(1440, Math.floor(Number(ans) || 0)));
          await api.myDay.sessionStop(taskId, { minutes_override: mins });
          reload(root);
        } else if (action === 'session-discard') {
          if (!confirm('Сбросить таймер без записи времени в журнал? Используй это, если к задаче не приступал.')) return;
          await api.myDay.sessionDiscard(taskId);
          reload(root);
        } else if (action === 'complete') {
          const mins = prompt('Сколько минут потрачено всего? (можно 0)');
          if (mins == null) return;
          await api.completeTask(taskId, { actual_minutes_spent: Number(mins) || 0 });
          reload(root);
        } else if (action === 'transfer') {
          const newRid = prompt('ID нового ответственного (ресурса):');
          if (newRid == null) return;
          await api.patchTask(taskId, { owner_resource_id: Number(newRid) });
          reload(root);
        } else if (action === 'request-resource') {
          alert('Запрос дополнительного ресурса — в backlog. Пока пиши руководителю руками.');
        } else if (action === 'reschedule-week') {
          alert('Перепланирование недели — в backlog. Пока используй Gantt-экран для перетаскивания задач.');
        } else if (action === 'hero-send-comment') {
          const ta = root.querySelector('.md-hero-comment-input');
          const body = ta?.value?.trim();
          if (!body) { alert('Комментарий пустой'); return; }
          await api.taskComments.create(taskId, body);
          if (ta) ta.value = '';
          reload(root);
        } else if (action === 'block') {
          const reason = prompt('Причина блокировки:');
          if (!reason) return;
          await api.blockTask(taskId, { reason });
          reload(root);
        }
      } catch (err) {
        alert(`Ошибка: ${err.detail || err.message}`);
      }
    });
  });
}

function attachQueueHandlers(root) {
  // Inline edit %.
  root.querySelectorAll('.md-q-row .pct[contenteditable]').forEach((el) => {
    let original = el.textContent;
    el.addEventListener('focus', () => { original = el.textContent; });
    el.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); el.blur(); }
      if (e.key === 'Escape') { el.textContent = original; el.blur(); }
    });
    el.addEventListener('blur', async () => {
      const raw = el.textContent.replace(/[^\d]/g, '');
      const val = Math.max(0, Math.min(100, parseInt(raw, 10) || 0));
      el.textContent = val + '%';
      if (el.textContent === original) return;
      const taskId = Number(el.dataset.taskId);
      try {
        await api.patchTask(taskId, { work_pct: val });
      } catch (err) {
        el.textContent = original;
        alert(`Не удалось сохранить: ${err.detail || err.message}`);
      }
    });
  });

  // Click on task name → выбираем задачу в hero (вместо перехода на Gantt).
  // Ctrl/Cmd+Click — открыть в Gantt (старое поведение).
  root.querySelectorAll('.md-q-row .name .txt').forEach((el) => {
    el.addEventListener('click', (e) => {
      const row = el.closest('.md-q-row');
      const taskId = Number(row?.dataset.taskId);
      const pid = row?.dataset.projectId;
      if (e.ctrlKey || e.metaKey) {
        if (pid) router.push(`/gantt/${pid}`);
        return;
      }
      if (taskId) {
        _selectedTaskId = taskId;
        // Silent re-render — без full SPA reload.
        initMyDay(_currentContainer || row.closest('.my-day-root')?.parentElement || document.getElementById('app-main'), { silent: true });
        // Скролл в hero после re-render.
        setTimeout(() => {
          const hero = document.querySelector('.md-hero');
          if (hero) hero.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 50);
      }
    });
  });

  // Group collapse toggle.
  root.querySelectorAll('.md-q-group .toggle').forEach((tog) => {
    tog.addEventListener('click', () => {
      const group = tog.closest('.md-q-group');
      const pid = group.dataset.projectId;
      const open = tog.textContent === '▾';
      tog.textContent = open ? '▸' : '▾';
      // Свернуть и строки задач, и подзаголовки сводных задач проекта.
      root.querySelectorAll(`.md-q-row[data-project-id="${pid}"]`).forEach((r) => {
        r.classList.toggle('collapsed', open);
      });
      root.querySelectorAll(`.md-q-subgroup[data-project-id="${pid}"]`).forEach((s) => {
        s.classList.toggle('collapsed', open);
      });
    });
  });

  // Свёртывание подзаголовка сводной задачи — прячет её листовые строки.
  root.querySelectorAll('.md-q-subgroup .toggle').forEach((tog) => {
    tog.addEventListener('click', () => {
      const sub = tog.closest('.md-q-subgroup');
      const parentId = sub.dataset.parentId;
      const open = tog.textContent === '▾';
      tog.textContent = open ? '▸' : '▾';
      root.querySelectorAll(`.md-q-row[data-parent-id="${parentId}"]`).forEach((r) => {
        r.classList.toggle('collapsed', open);
      });
    });
  });

  // Mini-Gantt bar click → /gantt/{project_id}.
  root.querySelectorAll('#md-gantt-svg [data-md-task-id]').forEach((g) => {
    g.addEventListener('click', () => {
      const pid = g.dataset.projectId;
      if (pid) router.push(`/gantt/${pid}`);
    });
  });
}

function attachActivityHandlers(root) {
  root.querySelectorAll('.md-feed-item').forEach((it) => {
    it.addEventListener('click', async () => {
      // Notification item: пометить прочитанным + перейти к задаче.
      const notifId = Number(it.dataset.notifId);
      if (notifId) {
        const projectId = it.dataset.projectId;
        const taskId = Number(it.dataset.taskId);
        try {
          await api.notifications.markRead(notifId);
        } catch { /* silent */ }
        if (projectId) {
          router.push(`/gantt/${projectId}`);
        } else if (taskId) {
          try {
            const t = await api.getTask(taskId);
            if (t?.project_id) router.push(`/gantt/${t.project_id}`);
          } catch { /* silent */ }
        }
        return;
      }
      // Audit-log item: переход по entity_type/entity_id.
      const type = it.dataset.entityType;
      const id = Number(it.dataset.entityId);
      if (type === 'task' && id) {
        try {
          const t = await api.getTask(id);
          if (t?.project_id) router.push(`/gantt/${t.project_id}`);
        } catch { /* silent */ }
      } else if (type === 'project' && id) {
        router.push(`/gantt/${id}`);
      }
    });
  });

  // Mark-all-read кнопка.
  const markAllBtn = root.querySelector('[data-md-action="mark-all-read"]');
  if (markAllBtn) {
    markAllBtn.addEventListener('click', async (e) => {
      e.stopPropagation();
      try {
        await api.notifications.markAllRead();
        router.push('/my-day');
      } catch (err) {
        alert(`Не удалось: ${err.detail || err.message}`);
      }
    });
  }
}

// ── Hero ticker ──────────────────────────────────────────────────────────
//
// Активная running-сессия — тикаем 1 раз в сек, HH:MM:SS = accumulated +
// (now - started_at). Пауза — статичное HH:MM:SS = accumulated.
// Нет сессии — статичный 00:00:00 (таймер привязан только к сессии).

function startHeroTicker(root, task) {
  if (!task) return;
  const clockEl = root.querySelector('.md-timer-clock');
  if (!clockEl) return;
  const hasSession = clockEl.dataset.mdHasSession === '1';
  const isPaused = clockEl.dataset.mdPaused === '1';
  const accMin = Number(clockEl.dataset.mdAccMin || '0');
  const startedAtIso = clockEl.dataset.mdStartedAt || '';
  const startedAtMs = startedAtIso ? new Date(startedAtIso).getTime() : null;

  // Таймер тикает ТОЛЬКО при активной running-сессии.
  // Нет сессии → статичный 00:00:00. Пауза → статичное accumulated.
  if (!hasSession) {
    clockEl.textContent = '00:00:00';
    return;
  }
  if (isPaused) {
    clockEl.textContent = formatHMS(accMin);
    return;
  }
  if (!startedAtMs) return;
  const tick = () => {
    const elapsedSec = Math.max(0, Math.floor((Date.now() - startedAtMs) / 1000));
    const totalSec = Math.max(0, Math.floor(accMin * 60) + elapsedSec);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    clockEl.textContent = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };
  tick();
  _heroTicker = setInterval(tick, 1000);
}

function reload(root) {
  // Silent re-render: дёргаем все endpoints и перерисовываем innerHTML
  // без loading-flash и без router.push. Сохраняем scroll position.
  // root === _currentContainer.
  initMyDay(root, { silent: true });
}

// ── Helpers ───────────────────────────────────────────────────────────────

function escHtml(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}
function escAttr(s) {
  return escHtml(s).replace(/"/g, '&quot;');
}

function formatRubShort(rub) {
  const n = Number(rub || 0);
  if (n >= 1_000_000) return Math.round(n / 100_000) / 10 + 'м';
  if (n >= 1_000) return Math.round(n / 1_000) + 'к';
  return String(Math.round(n));
}

function formatMins(mins) {
  const m = Math.max(0, Math.floor(Number(mins) || 0));
  if (m < 60) return `${m}<small> мин</small>`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? `${h}ч ${r}м` : `${h}<small> ч</small>`;
}


function formatDateShort(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.getDate()} ${MONTH_RU[d.getMonth()]}`;
}

function formatDueShort(iso) {
  if (!iso) return { text: '—', cls: '' };
  const d = new Date(iso);
  d.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((d.getTime() - today.getTime()) / 86400_000);
  if (diff < 0) return { text: `${diff} дн`, cls: 'past' };
  if (diff === 0) return { text: 'сегодня', cls: 'soon' };
  if (diff === 1) return { text: 'завтра', cls: 'soon' };
  if (diff <= 3) return { text: `${diff} дн`, cls: 'soon' };
  return { text: formatDateShort(iso), cls: '' };
}

// Утилизация > 999% — визуальный потолок (реальные данные в БД могут давать
// «5000%», это означает «planned >> capacity», смысла в точной цифре нет).
function clampPctDisplay(pct) {
  const v = Math.round(Number(pct) || 0);
  if (v > 999) return '999%+';
  return v + '%';
}

function formatRelative(iso) {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  const diffMin = Math.round((Date.now() - t) / 60_000);
  if (diffMin < 1) return 'только что';
  if (diffMin < 60) return `${diffMin} мин назад`;
  const diffHrs = Math.round(diffMin / 60);
  if (diffHrs < 24) return `${diffHrs} ч назад`;
  const diffDays = Math.round(diffHrs / 24);
  if (diffDays < 7) return `${diffDays} дн назад`;
  return formatDateShort(iso);
}
